/**
 * Auditoría — reader over the append-only audit log (compliance evidence). Paged
 * with a cursor ("Cargar más"), filterable by tipo de entidad and acción. Events
 * with attached evidence open the shared preview dialog (self-contained HTML);
 * events without evidence expand inline to show their before/after JSON states.
 * Gated to audit.list roles (tenant_admin, compliance_manager, auditor).
 */
import { Fragment, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { esPA, type AuditEventResponse } from '@cumpli/shared';
import { api } from '../lib/api';
import { formatDate } from '../lib/hooks';
import { auditActionLabel } from '../lib/audit-labels';
import { useToast } from '../lib/use-toast';
import { Badge, Button, Card, EmptyState, Input, Label, PageHeader, Skeleton } from '../components/ui';
import { DocumentPreviewDialog } from '../components/DocumentPreview';
import { ScrollText, FileSearch, ChevronDown, Paperclip, Download } from 'lucide-react';

// Date-only input -> ISO range covering the whole local calendar day.
function startOfDayIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}
function endOfDayIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

const PAGE_SIZE = 25;

export default function Auditoria() {
  const toast = useToast();

  // Draft (input) vs applied (query) filters, so typing doesn't refetch on every keystroke.
  const [draftEntity, setDraftEntity] = useState('');
  const [draftAction, setDraftAction] = useState('');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [expanded, setExpanded] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<{ eventKey: string; title: string } | null>(null);
  const [downloading, setDownloading] = useState<'csv' | 'pdf' | null>(null);

  const from = fromDate ? startOfDayIso(fromDate) : '';
  const to = toDate ? endOfDayIso(toDate) : '';

  const query = useInfiniteQuery({
    queryKey: ['audit', entityType, action, from, to],
    queryFn: ({ pageParam }) =>
      api.listAudit({
        limit: PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(entityType ? { entityType } : {}),
        ...(action ? { action } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  const applyFilters = () => {
    setEntityType(draftEntity.trim());
    setAction(draftAction.trim());
    setFromDate(draftFrom);
    setToDate(draftTo);
  };
  const clearFilters = () => {
    setDraftEntity('');
    setDraftAction('');
    setDraftFrom('');
    setDraftTo('');
    setEntityType('');
    setAction('');
    setFromDate('');
    setToDate('');
  };

  const downloadReport = async (format: 'csv' | 'pdf') => {
    setDownloading(format);
    try {
      const blob = await api.downloadAuditReport({
        ...(entityType ? { entityType } : {}),
        ...(action ? { action } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        format,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `auditoria-${today}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ message: 'No se pudo descargar el reporte de auditoría', kind: 'error' });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Auditoría"
        subtitle="Registro inalterable de todas las acciones. Evidencia y cambios verificables."
      />

      {/* filters */}
      <Card className="mb-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor="au-entity">Tipo de entidad</Label>
            <Input
              id="au-entity"
              placeholder="expediente, user, document…"
              value={draftEntity}
              onChange={(e) => setDraftEntity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div>
            <Label htmlFor="au-action">Acción</Label>
            <Input
              id="au-action"
              placeholder="expediente.update, validation.run…"
              value={draftAction}
              onChange={(e) => setDraftAction(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div>
            <Label htmlFor="au-from">Desde</Label>
            <Input
              id="au-from"
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="au-to">Hasta</Label>
            <Input
              id="au-to"
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={applyFilters}>Aplicar</Button>
          <Button variant="outline" onClick={clearFilters}>
            Limpiar
          </Button>
          <Button
            variant="outline"
            className="sm:ml-auto"
            onClick={() => void downloadReport('csv')}
            disabled={downloading !== null}
          >
            <Download className="h-4 w-4" strokeWidth={2} />
            {downloading === 'csv' ? 'Descargando…' : 'Descargar reporte (CSV)'}
          </Button>
          <Button
            variant="outline"
            onClick={() => void downloadReport('pdf')}
            disabled={downloading !== null}
          >
            <Download className="h-4 w-4" strokeWidth={2} />
            {downloading === 'pdf' ? 'Descargando…' : esPA.auditoria.downloadPdf}
          </Button>
        </div>
      </Card>

      {query.isLoading ? (
        <div className="grid gap-3">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : query.isError ? (
        <Card>
          <p className="text-sm text-tone-red">No se pudo cargar la auditoría.</p>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileSearch className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin eventos"
            hint="No hay eventos de auditoría que coincidan con los filtros."
          />
        </Card>
      ) : (
        <>
          {/* mobile: stacked cards (no horizontal scroll) */}
          <div data-testid="audit-cards" className="grid gap-2.5 sm:hidden">
            {items.map((ev) => {
              const hasStates = ev.beforeState !== undefined || ev.afterState !== undefined;
              const isOpen = expanded === ev.eventKey;
              return (
                <div key={ev.eventKey} className="rounded-2xl border border-line bg-panel p-4 shadow-card">
                  <div className="flex items-start justify-between gap-2">
                    <span className="break-all font-mono text-[12px] font-semibold text-ink">
                      {auditActionLabel(ev.action)}
                    </span>
                    <span className="shrink-0 text-[12px] text-ink-soft">
                      {formatDate(ev.createdAt, '—')}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Badge muted>{ev.entityType}</Badge>
                    <span className="break-all font-mono text-[11px] text-ink-faint">{ev.entityId}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
                    <span className="text-sm text-ink">{ev.actorName}</span>
                    <span className="font-mono text-[11px] text-ink-faint">{ev.actorRole}</span>
                  </div>
                  <div className="mt-2">
                    <DetalleAction
                      ev={ev}
                      isOpen={isOpen}
                      onToggle={() => setExpanded(isOpen ? null : ev.eventKey)}
                      onEvidence={() =>
                        setEvidence({ eventKey: ev.eventKey, title: ev.evidenceName ?? ev.action })
                      }
                    />
                  </div>
                  {isOpen && hasStates && (
                    <div className="mt-3 grid gap-4">
                      <StateBlock title="Antes" value={ev.beforeState} />
                      <StateBlock title="Después" value={ev.afterState} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* desktop: table */}
          <Card className="hidden overflow-hidden p-0 sm:block">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] table-fixed text-sm">
                <colgroup>
                  <col className="w-[120px]" />
                  <col />
                  <col />
                  <col className="w-[18%]" />
                  <col className="w-[132px]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] uppercase tracking-wider text-ink-faint">
                    <th className="px-4 py-3 font-semibold">Fecha</th>
                    <th className="px-4 py-3 font-semibold">Acción</th>
                    <th className="px-4 py-3 font-semibold">Entidad</th>
                    <th className="px-4 py-3 font-semibold">Actor</th>
                    <th className="px-4 py-3 font-semibold text-right">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((ev) => {
                    const hasStates = ev.beforeState !== undefined || ev.afterState !== undefined;
                    const isOpen = expanded === ev.eventKey;
                    return (
                      <Fragment key={ev.eventKey}>
                        <tr className="border-b border-line last:border-0 align-top">
                          <td className="px-3 py-3 text-[13px] text-ink-soft">
                            {formatDate(ev.createdAt, '—')}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className="block truncate font-mono text-[12px] font-semibold text-ink"
                              title={ev.action}
                            >
                              {auditActionLabel(ev.action)}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex min-w-0 flex-col gap-0.5">
                              <Badge muted>{ev.entityType}</Badge>
                              <span
                                className="block truncate font-mono text-[11px] text-ink-faint"
                                title={ev.entityId}
                              >
                                {ev.entityId}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="truncate text-ink" title={ev.actorName}>{ev.actorName}</div>
                            <div className="truncate font-mono text-[11px] text-ink-faint" title={ev.actorRole}>
                              {ev.actorRole}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <DetalleAction
                              ev={ev}
                              isOpen={isOpen}
                              onToggle={() => setExpanded(isOpen ? null : ev.eventKey)}
                              onEvidence={() =>
                                setEvidence({ eventKey: ev.eventKey, title: ev.evidenceName ?? ev.action })
                              }
                            />
                          </td>
                        </tr>
                        {isOpen && hasStates && (
                          <tr className="border-b border-line bg-canvas">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="grid gap-4 md:grid-cols-2">
                                <StateBlock title="Antes" value={ev.beforeState} />
                                <StateBlock title="Después" value={ev.afterState} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {query.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
          </Button>
        </div>
      )}

      {evidence && (
        <DocumentPreviewDialog
          title={evidence.title}
          cacheKey={evidence.eventKey}
          fetchUrl={() => api.getAuditEvidenceUrl(evidence.eventKey)}
          onClose={() => setEvidence(null)}
        />
      )}
    </div>
  );
}

function StateBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
        <ScrollText className="h-3.5 w-3.5" strokeWidth={2} /> {title}
      </div>
      <pre className="max-h-64 overflow-auto rounded-lg border border-line bg-panel p-3 font-mono text-[12px] leading-relaxed text-ink">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/**
 * The three-way "Detalle" action shared by the desktop table cell and the
 * mobile card: evidence link, expand-changes toggle, or a bare dash when
 * neither applies. Kept as a single source so both layouts stay in sync.
 */
function DetalleAction({
  ev,
  isOpen,
  onToggle,
  onEvidence,
}: {
  ev: AuditEventResponse;
  isOpen: boolean;
  onToggle: () => void;
  onEvidence: () => void;
}) {
  const hasStates = ev.beforeState !== undefined || ev.afterState !== undefined;
  if (ev.hasEvidence) {
    return (
      <button
        onClick={onEvidence}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-dark"
      >
        <Paperclip className="h-4 w-4" strokeWidth={2} /> Ver evidencia
      </button>
    );
  }
  if (hasStates) {
    return (
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-ink"
      >
        Ver cambios
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>
    );
  }
  return <span className="text-xs text-ink-faint">—</span>;
}
